// components/templates/MultiServiceTemplate.tsx
import React, { useState, useEffect, useRef } from "react";
import {
    MapPin,
    Phone,
    Mail,
    Globe,
    Upload,
    Image as ImageIcon,
    Briefcase,
    X,
    ExternalLink,
    Plus,
    Sparkles,
    Loader2,
    Crop,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import { ImageCropModal } from "@/components/ui/ImageCropModal";

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Copied
// verbatim from the single-listing templates' own getEditableImageUrl helper.
const getEditableImageUrl = (url: string): string => {
    if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
    try {
        const parsed = new URL(url, window.location.origin);
        const needsProxy =
            parsed.hostname.includes("amazonaws.com") ||
            parsed.hostname.includes("mylistiq-production.s3");
        if (!needsProxy) return url;
        return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
    } catch {
        return url;
    }
};

// ─── Helper: convert the crop modal's output data URL into a File, the same
// way a freshly-picked photo already becomes a File from a file input.
const dataUrlToFile = (dataUrl: string, filename: string): File => {
    const arr = dataUrl.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    const mime = mimeMatch ? mimeMatch[1] : "image/png";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) {
        u8arr[n] = bstr.charCodeAt(n);
    }
    return new File([u8arr], filename, { type: mime });
};

interface FeatureItem {
    id?: number;
    feature_name: string;
    name?: string;
}

interface MultiServiceTemplateProps {
    index: number;
    service: {
        id: string;
        image?: string;
        original_hero_image?: string;
        title: string;
        service_type: string;
        description?: string;
        phone?: string;
        email?: string;
        website?: string;
        address?: string;
        photos?: string[];
        features?: FeatureItem[];
    };
    onContact?: () => void;
    onChange?: (fields: Partial<MultiServiceTemplateData>) => void;
    onImageChange?: (file: File, type: 'main' | 'secondary') => void;
    imageUploading?: boolean;
    readOnly?: boolean;
    getFullUrl?: (url: string | null | undefined) => string;
}

// In MultiServiceTemplate.tsx, make sure the interface includes features:
export interface MultiServiceTemplateData {
    title: string;
    service_type: string;
    description: string;
    phone: string;
    email: string;
    website: string;
    address: string;
    image: string;
    features?: Array<{ id?: number; feature_name: string; name?: string }>;
}

const MAX_FEATURES = 10;

const MultiServiceTemplate: React.FC<MultiServiceTemplateProps> = ({
    index,
    service,
    onContact,
    onChange,
    onImageChange,
    imageUploading = false,
    readOnly = false,
    getFullUrl = (url) => url || "",
}) => {
    const { toast } = useToast();
    const mainImageRef = useRef<HTMLInputElement>(null);
    const secondaryImageRef = useRef<HTMLInputElement>(null);

    // Local state for editable fields
    const [localData, setLocalData] = useState({
        title: service.title || "",
        service_type: service.service_type || "",
        description: service.description || "",
        phone: service.phone || "",
        email: service.email || "",
        website: service.website || "",
        address: service.address || "",
        image: service.image || "",
    });

    const [localFeatures, setLocalFeatures] = useState<FeatureItem[]>(
        service.features || []
    );

    const [secondaryImage, setSecondaryImage] = useState<string>(
        service.photos?.[0] || ""
    );

    // Sync with props when they change (preserve in-progress image preview)
    useEffect(() => {
        setLocalData(prev => ({
            title: service.title || "",
            service_type: service.service_type || "",
            description: service.description || "",
            phone: service.phone || "",
            email: service.email || "",
            website: service.website || "",
            address: service.address || "",
            image: service.image || prev.image || "",
        }));
        setLocalFeatures(service.features || []);
        setSecondaryImage(service.photos?.[0] || "");
    }, [
        service.id,
        service.title,
        service.service_type,
        service.description,
        service.phone,
        service.email,
        service.website,
        service.address,
        service.image,
        service.photos,
        service.features,
    ]);

    // Handle input change
    const handleInputChange = (field: keyof MultiServiceTemplateData, value: any) => {
        if (readOnly) return;

        setLocalData(prev => ({
            ...prev,
            [field]: value,
        }));

        onChange?.({ [field]: value });
    };

    // Handle feature change
    const handleFeatureChange = (featureIndex: number, value: string) => {
        if (readOnly) return;

        const updatedFeatures = [...localFeatures];
        updatedFeatures[featureIndex] = {
            ...updatedFeatures[featureIndex],
            feature_name: value,
            name: value,
        };
        setLocalFeatures(updatedFeatures);
        onChange?.({ features: updatedFeatures });
    };

    // Add new feature
    const addFeature = () => {
        if (readOnly) return;
        if (localFeatures.length >= MAX_FEATURES) {
            toast({
                title: "Feature Limit Reached",
                description: `Maximum ${MAX_FEATURES} features allowed.`,
                variant: "destructive",
            });
            return;
        }

        const newFeatures = [...localFeatures, { feature_name: "", name: "" }];
        setLocalFeatures(newFeatures);
        onChange?.({ features: newFeatures });
    };

    // Remove feature
    const removeFeature = (featureIndex: number) => {
        if (readOnly) return;

        const updatedFeatures = localFeatures.filter((_, idx) => idx !== featureIndex);
        setLocalFeatures(updatedFeatures);
        onChange?.({ features: updatedFeatures });

        toast({
            title: "Feature Removed",
            description: "Feature has been deleted.",
        });
    };

    // Handle main image upload
    const handleMainImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast({
                title: "Invalid File",
                description: "Please upload an image file",
                variant: "destructive",
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: "File Too Large",
                description: "Image must be less than 10 MB",
                variant: "destructive",
            });
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setLocalData(prev => ({ ...prev, image: previewUrl }));
        onChange?.({ image: previewUrl });

        // A genuinely new photo pick invalidates any previously-captured
        // "original" — otherwise the next Edit click would keep re-opening
        // whatever photo was cropped before this replacement instead of the
        // photo the user just picked. Clearing the local cache here forces
        // openEditImage to re-derive it from the server (which the parent's
        // Save also refreshes to this new file, see MultiServicePreviewPage's
        // handleSaveEdit).
        setOriginalImage("");

        onImageChange?.(file, 'main');
        e.target.value = "";
    };

    // =========================================================================
    // EDIT IMAGE (crop/zoom the main photo, non-destructively)
    //
    // This is deliberately independent of the "Change Main Photo" flow above,
    // which only stages a File on the parent (committed later via "Save
    // Changes"). Edit Image instead saves immediately and directly to the
    // backend, exactly like every single-listing template's Edit Image
    // feature, so it can never interact with or be lost by the deferred
    // stage/Save/Cancel flow for the other fields.
    // =========================================================================
    const EDIT_CROP_W = 340;
    const EDIT_CROP_H = 340; // matches the 340x340 main-image box

    const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
        open: false,
        imageUrl: "",
    });
    const [originalImage, setOriginalImage] = useState<string>("");
    const [isSavingEditedImage, setIsSavingEditedImage] = useState(false);

    const openEditImage = async () => {
        if (readOnly || !localData.image) return;

        let originalSource = originalImage || (service as any)?.original_hero_image || "";

        if (!originalSource && service.id) {
            try {
                const res = await axiosInstance.get(`${endpoints.service_listings}${service.id}/`);
                originalSource = res?.data?.original_hero_image || "";
            } catch (err) {
                console.error("Failed to check for existing original hero image:", err);
            }
        }

        if (!originalSource) {
            // Truly never captured yet. Self-heal from the current image so this
            // and every future edit has a stable, full source to re-crop from.
            // Skip seeding if the current image is only a local, unsaved preview
            // (data:/blob:) — there's nothing on the server yet to attach it to.
            originalSource = localData.image;
            if (service.id && !localData.image.startsWith("data:") && !localData.image.startsWith("blob:")) {
                axiosInstance
                    .patch(`${endpoints.service_listings}${service.id}/`, { original_hero_image: localData.image })
                    .catch((err) => {
                        console.error("Failed to seed original hero image:", err);
                    });
            }
        }

        setOriginalImage(originalSource);
        setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
    };

    const handleEditImageConfirm = async (croppedDataUrl: string) => {
        setLocalData(prev => ({ ...prev, image: croppedDataUrl }));
        onChange?.({ image: croppedDataUrl });

        if (service.id) {
            setIsSavingEditedImage(true);
            try {
                const file = dataUrlToFile(croppedDataUrl, `edited-service-${Date.now()}.png`);
                const formData = new FormData();
                formData.append("hero_image", file);
                const response = await axiosInstance.patch(
                    `${endpoints.service_listings}${service.id}/`,
                    formData,
                    { headers: { "Content-Type": undefined as any }, transformRequest: [(data) => data] }
                );
                const heroUrl = response.data?.hero_image_url || response.data?.hero_image;
                if (heroUrl) {
                    setLocalData(prev => ({ ...prev, image: heroUrl }));
                    onChange?.({ image: heroUrl });
                }
            } catch (err) {
                console.error("Failed to save edited image:", err);
                toast({
                    title: "Warning",
                    description: "Image preview updated but saving the crop failed",
                    variant: "destructive",
                    duration: 1500,
                });
            } finally {
                setIsSavingEditedImage(false);
            }
        }

        setCropModal({ open: false, imageUrl: "" });
    };

    const handleEditImageCancel = () => {
        setCropModal({ open: false, imageUrl: "" });
    };

    // Handle secondary image upload
    const handleSecondaryImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (!file.type.startsWith("image/")) {
            toast({
                title: "Invalid File",
                description: "Please upload an image file",
                variant: "destructive",
            });
            return;
        }

        if (file.size > 10 * 1024 * 1024) {
            toast({
                title: "File Too Large",
                description: "Image must be less than 10 MB",
                variant: "destructive",
            });
            return;
        }

        const previewUrl = URL.createObjectURL(file);
        setSecondaryImage(previewUrl);
        onImageChange?.(file, 'secondary');
        e.target.value = "";
    };

    // Remove secondary image
    const handleRemoveSecondaryImage = () => {
        setSecondaryImage("");
    };

    // Service types
    const serviceTypes = [
        "Real Estate Agent",
        "Mortgage Broker",
        "Home Inspector",
        "Contractor",
        "Interior Designer",
        "Landscaper",
        "Plumbing",
        "Electrical",
        "HVAC",
        "Roofing",
        "Painting",
        "Cleaning",
        "Photography",
        "Staging",
        "General Contractor",
        "Cabinetry",
        "Junk Removal",
        "Wholesaler",
        "Flooring",
        "Pest Control",
        "Moving Services",
        "Property Management",
        "Other",
    ];

    return (
        <div className="w-full">
            <div
                className="template-preview-card bg-white rounded-xl sm:rounded-2xl shadow-lg border mx-auto inline-block align-top overflow-hidden mb-3"
                style={{ minWidth: "760px" }}
            >
                {/* Service Number Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-4 py-3 sm:px-6 flex items-center gap-3 min-w-0 overflow-hidden">
                    <div className="flex h-9 w-9 sm:h-10 sm:w-10 items-center justify-center rounded-full bg-white text-blue-600 font-bold text-base sm:text-lg flex-shrink-0">
                        {index}
                    </div>
                    <div className="text-white min-w-0 flex-1 overflow-hidden">
                        <p className="text-xs sm:text-sm opacity-80">Service</p>
                        {readOnly ? (
                            <p
                                className="font-semibold text-sm sm:text-base truncate"
                                title={localData.title || `Service #${index}`}
                            >
                                {localData.title || `Service #${index}`}
                            </p>
                        ) : (
                            <input
                                type="text"
                                value={localData.title}
                                onChange={(e) => handleInputChange("title", e.target.value)}
                                className="font-semibold bg-transparent border-none outline-none text-white placeholder-white/60 w-full min-w-0 text-sm sm:text-base truncate"
                                placeholder="Service Title"
                            />
                        )}
                    </div>
                </div>

                <div className="grid grid-cols-[340px_minmax(420px,1fr)] gap-0 items-stretch">

                    {/* Left - Images */}
                    {/* <div className="relative h-full"> */}
                    <div className="relative flex-shrink-0">
                        {/* Main Image */}
                        <div className="relative w-full  group"
                            style={{
                                height: '340px', minHeight: '340px', maxHeight: '340px', width: '340px', minWidth: '340px', maxWidth: '340px'
                            }}>

                            {localData.image ? (
                                <>
                                    <img
                                        key={localData.image}
                                        src={getFullUrl(localData.image)}
                                        alt="Service"
                                        className="absolute inset-0 w-full h-full object-cover object-center"
                                        onError={(e) => {
                                            (e.target as HTMLImageElement).style.display = 'none';
                                        }}
                                    />
                                    {imageUploading && (
                                        <div className="absolute inset-0 z-10 bg-black/60 flex flex-col items-center justify-center text-white gap-2">
                                            <Loader2 className="h-8 w-8 animate-spin" />
                                            <p className="text-sm font-semibold">Uploading image…</p>
                                        </div>
                                    )}
                                    {!readOnly && (
                                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-all duration-300 flex items-center justify-center gap-2">
                                            <Button
                                                size="sm"
                                                onClick={() => !imageUploading && mainImageRef.current?.click()}
                                                className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                                                disabled={imageUploading}
                                            >
                                                <Upload className="h-4 w-4 mr-2" />
                                                Change Main Photo
                                            </Button>
                                            <Button
                                                size="sm"
                                                onClick={() => !imageUploading && !isSavingEditedImage && openEditImage()}
                                                className="bg-white/20 backdrop-blur-sm text-white border border-white/40 hover:bg-white/30"
                                                disabled={imageUploading || isSavingEditedImage}
                                            >
                                                <Crop className="h-4 w-4 mr-2" />
                                                Edit
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <label
                                    className={`absolute inset-0 flex flex-col items-center justify-center bg-gray-100 ${!readOnly ? "cursor-pointer hover:bg-gray-200" : ""} transition-colors`}
                                    onClick={() => !readOnly && mainImageRef.current?.click()}
                                >
                                    <ImageIcon className="h-16 w-16 text-gray-400 mb-3" />
                                    <p className="text-gray-500 font-medium">
                                        {readOnly ? "No Image" : "Upload Main Photo"}
                                    </p>
                                </label>
                            )}

                            <input
                                ref={mainImageRef}
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={handleMainImageChange}
                                disabled={readOnly || imageUploading}
                            />

                            {!readOnly && (
                                <ImageCropModal
                                    open={cropModal.open}
                                    imageUrl={cropModal.imageUrl}
                                    title="Adjust Main Photo"
                                    outputSize={700}
                                    cropW={EDIT_CROP_W}
                                    cropH={EDIT_CROP_H}
                                    onConfirm={handleEditImageConfirm}
                                    onCancel={handleEditImageCancel}
                                />
                            )}
                        </div>
                    </div>

                    {/* Right - Content */}
                    {/* <div className="flex flex-col p-3 sm:p-4 md:p-6 min-w-0 overflow-hidden"> */}
                    <div className="flex min-w-0 w-full max-w-none flex-col p-3 sm:p-4 md:p-6 overflow-hidden">

                        {/* Service Type */}
                        <div>
                            <div className="mb-4">
                                <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
                                    Service Type
                                </p>
                                {readOnly ? (
                                    <p className="font-semibold text-gray-900">{localData.service_type || "N/A"}</p>
                                ) : (
                                    <select
                                        value={localData.service_type}
                                        onChange={(e) => handleInputChange("service_type", e.target.value)}
                                        className="w-full font-semibold text-gray-900 bg-transparent border border-gray-200 rounded-lg p-2 outline-none focus:border-blue-500"
                                    >
                                        <option value="">Select Service Type</option>
                                        {serviceTypes.map((type) => (
                                            <option key={type} value={type}>{type}</option>
                                        ))}
                                    </select>
                                )}
                            </div>

                            {/* Description */}
                            <div className="mb-4">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                    Description
                                </p>
                                {readOnly ? (
                                    <p className="text-sm text-gray-600 line-clamp-3 sm:line-clamp-4 break-words overflow-hidden">
                                        {localData.description || "No description available"}
                                    </p>
                                ) : (
                                    <textarea
                                        value={localData.description}
                                        onChange={(e) => handleInputChange("description", e.target.value)}
                                        className="w-full text-sm text-gray-600 bg-transparent border border-gray-200 rounded-lg p-2 outline-none focus:border-blue-500 resize-none"
                                        rows={4}
                                        placeholder="Service description..."
                                    />
                                )}
                            </div>

                            {/* Features - Editable */}
                            <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1">
                                        {/* <Sparkles className="h-3 w-3 text-blue-600" /> */}
                                        Key Features
                                    </p>
                                    {!readOnly && (
                                        <span className="text-xs text-gray-400">
                                            {localFeatures.length}/{MAX_FEATURES}
                                        </span>
                                    )}
                                </div>

                                {readOnly ? (
                                    // Read-only features display
                                    localFeatures.length > 0 ? (
                                        <ul className="space-y-2">
                                            {localFeatures.slice(0, 5).map((feature, idx) => {
                                                const featureName = typeof feature === 'string'
                                                    ? feature
                                                    : (feature.feature_name || feature.name || '');
                                                return (
                                                    <li key={idx} className="flex items-center gap-2 text-sm text-gray-700 min-w-0 overflow-hidden">
                                                        <span className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0"></span>
                                                        <span className="truncate" title={featureName}>{featureName}</span>
                                                    </li>
                                                );
                                            })}
                                            {localFeatures.length > 5 && (
                                                <li className="text-xs text-gray-500">
                                                    +{localFeatures.length - 5} more features
                                                </li>
                                            )}
                                        </ul>
                                    ) : (
                                        <p className="text-sm text-gray-400 italic">No features added</p>
                                    )
                                ) : (
                                    // Editable features
                                    <div className="space-y-2">
                                        {localFeatures.map((feature, idx) => {
                                            const featureName = typeof feature === 'string'
                                                ? feature
                                                : (feature.feature_name || feature.name || '');
                                            return (
                                                <div key={feature.id || idx} className="flex items-center gap-1.5 sm:gap-2 group min-w-0">
                                                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center">
                                                        <span className="text-xs font-medium text-blue-600">{idx + 1}</span>
                                                    </div>
                                                    <Input
                                                        type="text"
                                                        value={featureName}
                                                        onChange={(e) => handleFeatureChange(idx, e.target.value)}
                                                        placeholder="Feature name..."
                                                        className="flex-1 min-w-0 h-8 text-xs sm:text-sm"
                                                    />
                                                    <button
                                                        type="button"
                                                        onClick={() => removeFeature(idx)}
                                                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity text-red-500 hover:text-red-700 p-1"
                                                    >
                                                        <X className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                                                    </button>
                                                </div>
                                            );
                                        })}

                                        {/* Add Feature Button */}
                                        {localFeatures.length < MAX_FEATURES && (
                                            <button
                                                type="button"
                                                onClick={addFeature}
                                                className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 py-1"
                                            >
                                                <Plus className="h-4 w-4" />
                                                Add Feature
                                            </button>
                                        )}
                                    </div>
                                )}
                            </div>



                            {/* Website Link Field */}
                            {!readOnly && (
                                <div className="mb-4">
                                    <div className="flex items-start gap-2 min-w-0 overflow-hidden">
                                        <Globe className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600 flex-shrink-0 mt-1" />
                                        <div className="flex-1 min-w-0 overflow-hidden">
                                            <input
                                                type="url"
                                                value={localData.website}
                                                onChange={(e) => handleInputChange("website", e.target.value)}
                                                className="text-sm font-medium text-gray-900 bg-transparent border-b border-gray-200 outline-none w-full focus:border-blue-500 pb-1 placeholder-gray-400 truncate"
                                                placeholder="https://example.com"
                                            />
                                            <p className="text-xs text-gray-400 mt-1">
                                                This link will open in a new tab when "View Service" is clicked
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Action Buttons */}
                        <div className="template-preview-actions mt-4 flex gap-3">
                            {/* View Service Button - only show when a valid website exists */}
                            {localData.website?.trim() && (
                                <a
                                    href={localData.website.startsWith('http') ? localData.website : `https://${localData.website}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-500 to-blue-600 px-4 py-3 text-sm font-semibold text-white hover:from-blue-600 hover:to-blue-700 transition w-full"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    View Service

                                </a>
                            )}

                            {/* <button
                            onClick={(e) => {
                                e.stopPropagation();
                                onContact?.();
                            }}
                            className="flex flex-1 items-center justify-center rounded-xl border border-blue-500 px-4 py-3 text-sm font-semibold text-blue-600 hover:bg-blue-50 transition"
                        >
                            Contact Details
                        </button> */}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MultiServiceTemplate;
